import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Send, UserCheck, Search, Sparkles, FileText, ArrowUpRight, Clock, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const STAGE_CONFIG = {
  invited: { label: "Invited", className: "bg-slate-100 text-slate-600", icon: Send, step: 1 },
  registered: { label: "Registered", className: "bg-blue-100 text-blue-700", icon: UserCheck, step: 2 },
  active: { label: "Active", className: "bg-teal-100 text-teal-700", icon: UserCheck, step: 2 },
  searching: { label: "Searching", className: "bg-amber-100 text-amber-700", icon: Search, step: 3 },
  deep_analysis: { label: "Ran Analysis", className: "bg-lime-100 text-lime-700", icon: Sparkles, step: 4 },
  work_order: { label: "Submitted WO", className: "bg-evergreen-100 text-evergreen-700", icon: FileText, step: 5 },
};

function StagePipeline({ stage }) {
  const steps = [
    { key: "invited", label: "Invited" },
    { key: "registered", label: "Registered" },
    { key: "searching", label: "Searching" },
    { key: "deep_analysis", label: "Analyzed" },
    { key: "work_order", label: "Work Order" },
  ];
  const currentStep = STAGE_CONFIG[stage]?.step || 1;
  return (
    <div className="flex items-center gap-0.5">
      {steps.map((s, i) => {
        const reached = (i + 1) <= currentStep;
        return (
          <div key={s.key} className="flex items-center gap-0.5">
            <div className={`w-2 h-2 rounded-full transition-colors ${reached ? "bg-lime-500" : "bg-slate-200"}`} title={s.label} />
            {i < steps.length - 1 && <div className={`w-4 h-0.5 ${reached ? "bg-lime-400" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function DemoFunnel() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/demo-funnel").then(r => setRows(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [api]); // eslint-disable-line

  // Summary stats
  const total = rows.length;
  const registered = rows.filter(r => r.stage !== "invited").length;
  const searching = rows.filter(r => ["searching", "deep_analysis", "work_order"].includes(r.stage)).length;
  const analyzed = rows.filter(r => ["deep_analysis", "work_order"].includes(r.stage)).length;
  const woSubmitted = rows.filter(r => r.stage === "work_order").length;
  const upgraded = rows.filter(r => r.is_upgraded).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="demo-funnel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading text-slate-900">Demo Funnel</h1>
          <p className="text-sm text-slate-500 mt-1">Track every demo invite from sent to converted</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCcw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {/* Funnel Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[
          { label: "Invited", value: total, className: "text-slate-900" },
          { label: "Registered", value: registered, className: "text-blue-700" },
          { label: "Searching", value: searching, className: "text-amber-700" },
          { label: "Ran Analysis", value: analyzed, className: "text-lime-700" },
          { label: "Submitted WO", value: woSubmitted, className: "text-evergreen-950" },
          { label: "Upgraded to Paid", value: upgraded, className: "text-teal-700" },
        ].map(s => (
          <Card key={s.label} className="rounded-xl border">
            <CardContent className="p-4 text-center">
              <span className={`text-2xl font-accent font-bold ${s.className}`}>{s.value}</span>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Conversion rates */}
      {total > 0 && (
        <div className="flex items-center gap-6 mb-6 px-4 py-3 bg-white rounded-xl border text-sm text-slate-600">
          <span>Invite-to-Register: <strong className="text-slate-900">{total > 0 ? Math.round(registered / total * 100) : 0}%</strong></span>
          <span className="text-slate-300">|</span>
          <span>Register-to-Search: <strong className="text-slate-900">{registered > 0 ? Math.round(searching / registered * 100) : 0}%</strong></span>
          <span className="text-slate-300">|</span>
          <span>Search-to-Analysis: <strong className="text-slate-900">{searching > 0 ? Math.round(analyzed / searching * 100) : 0}%</strong></span>
          <span className="text-slate-300">|</span>
          <span>Analysis-to-WO: <strong className="text-slate-900">{analyzed > 0 ? Math.round(woSubmitted / analyzed * 100) : 0}%</strong></span>
          <span className="text-slate-300">|</span>
          <span>Demo-to-Paid: <strong className="text-lime-700">{total > 0 ? Math.round(upgraded / total * 100) : 0}%</strong></span>
        </div>
      )}

      {/* Invite Rows */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <Card className="border-dashed border-slate-300 bg-slate-50/50">
          <CardContent className="p-12 text-center">
            <Send className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-heading text-slate-900 mb-2">No demo invites yet</h3>
            <p className="text-sm text-slate-500">Use "Invite to Demo" in Client Management to send your first demo invite.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Prospect</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Organization</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Stage</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Pipeline</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">Logins</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">Searches</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">Analyses</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">WOs</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const stageConf = STAGE_CONFIG[r.stage] || STAGE_CONFIG.invited;
                return (
                  <tr key={r.email} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`demo-row-${r.email}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{r.name || r.email.split("@")[0]}</p>
                      <p className="text-xs text-slate-400 font-mono">{r.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-700">{r.org_name}</span>
                      {r.is_upgraded && <Badge className="bg-lime-100 text-lime-700 border-0 text-[10px] ml-1.5">PAID</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${stageConf.className} border-0 text-xs`}>{stageConf.label}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <StagePipeline stage={r.stage} />
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-slate-600">{r.logins || 0}</td>
                    <td className="px-4 py-3 text-center font-mono text-slate-600">{r.searches || 0}</td>
                    <td className="px-4 py-3 text-center font-mono text-slate-600">{r.deep_analyses || 0}</td>
                    <td className="px-4 py-3 text-center font-mono font-bold text-evergreen-950">{r.work_orders || 0}</td>
                    <td className="px-4 py-3">
                      {r.last_active ? (
                        <div>
                          <p className="text-xs text-slate-600">{timeAgo(r.last_active)}</p>
                          {r.days_since_invite != null && r.stage === "invited" && r.days_since_invite > 3 && (
                            <p className="text-[10px] text-amber-600 flex items-center gap-0.5 mt-0.5"><Clock className="w-3 h-3" /> {r.days_since_invite}d since invite</p>
                          )}
                        </div>
                      ) : (
                        <div>
                          <p className="text-xs text-slate-400">No activity</p>
                          {r.days_since_invite != null && r.days_since_invite > 3 && (
                            <p className="text-[10px] text-amber-600 flex items-center gap-0.5 mt-0.5"><Clock className="w-3 h-3" /> {r.days_since_invite}d since invite</p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
